/**
 * Sprint 5 client feedback 2026-07-07 (UI Issues #7) — a single
 * reusable status-chip component so unmapped / unexpected status
 * values ("Not Found", "Unknown", "N/A", …) render as a neutral chip
 * instead of raw text.
 *
 * Rationale: report tables had a mix of mapped statuses rendered as
 * styled chips (Pass, High, Critical, …) and unmapped values rendered
 * as plain text, breaking the visual grid. This component:
 *
 *   1. Looks up the value in a canonical known-status map.
 *   2. If found, renders the mapped chip with its existing style.
 *   3. If NOT found, renders the value inside a neutral `badge-neutral`
 *      chip so the UI stays uniform.
 *
 * The component is intentionally chatty about preserving the
 * existing look for known values (nothing about those chips
 * changes) — only unmapped values get the neutral treatment.
 *
 * Do NOT use for statuses that already carry a bespoke inline chip
 * (e.g. §14 signals with `{sig.icon} {sig.label}`), just for plain
 * text status renders.
 */

import type { JSX } from 'react';

/** Canonical status → badge class map. Kept in one place so future
 *  status additions land here and the visual language stays uniform. */
const KNOWN_STATUS_MAP: Record<string, { badgeClass: string; label?: string }> = {
  // Assessment lifecycle statuses
  'completed': { badgeClass: 'badge-pass', label: 'Completed' },
  'complete': { badgeClass: 'badge-pass', label: 'Completed' },
  'in progress': { badgeClass: 'badge-watch', label: 'In Progress' },
  'in_progress': { badgeClass: 'badge-watch', label: 'In Progress' },
  'analysing': { badgeClass: 'badge-watch', label: 'Analysing' },
  'analyzing': { badgeClass: 'badge-watch', label: 'Analyzing' },
  'ingesting': { badgeClass: 'badge-neutral', label: 'Ingesting' },
  'pending': { badgeClass: 'badge-neutral', label: 'Pending' },
  'cancelled': { badgeClass: 'badge-neutral', label: 'Cancelled' },
  'failed': { badgeClass: 'badge-fail', label: 'Failed' },
  'fail': { badgeClass: 'badge-fail', label: 'Fail' },

  // Overall / verification outcomes
  'pass': { badgeClass: 'badge-pass', label: 'Pass' },
  'partial': { badgeClass: 'badge-watch', label: 'Partial' },
  'flag': { badgeClass: 'badge-watch', label: 'Flag' },
  'flagged': { badgeClass: 'badge-watch', label: 'Flagged' },
  'supported': { badgeClass: 'badge-pass', label: 'Supported' },
  'extracted': { badgeClass: 'badge-pass', label: 'Extracted' },
  'derived': { badgeClass: 'badge-info', label: 'Derived' },

  // Severity levels
  'critical': { badgeClass: 'badge-fail', label: 'Critical' },
  'high': { badgeClass: 'badge-fail', label: 'High' },
  'medium': { badgeClass: 'badge-watch', label: 'Medium' },
  'low': { badgeClass: 'badge-info', label: 'Low' },
  'watch': { badgeClass: 'badge-watch', label: 'Watch' },

  // Sprint 5 SPA §4 category assignments — kept in the map so they
  // continue rendering with their existing colours rather than
  // falling into the neutral bucket.
  'standard': { badgeClass: 'badge-pass', label: 'Standard' },
  'aggressive': { badgeClass: 'badge-fail', label: 'Aggressive' },
  'investor-friendly': { badgeClass: 'badge-pass', label: 'Investor-friendly' },
  'conservative': { badgeClass: 'badge-info', label: 'Conservative' },
  'inadequate': { badgeClass: 'badge-fail', label: 'Inadequate' },
  'not applicable': { badgeClass: 'badge-neutral', label: 'Not Applicable' },
  'present by reference': { badgeClass: 'badge-info', label: 'Present by reference' },
  'cannot assess': { badgeClass: 'badge-watch', label: 'Cannot Assess' },
  'present': { badgeClass: 'badge-pass', label: 'Present' },
  'not present': { badgeClass: 'badge-fail', label: 'Not Present' },
};

/** Normalise raw string values so the map lookup is
 *  case- and whitespace-insensitive. */
function normalise(v: string): string {
  return v.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

interface StatusChipProps {
  /** Raw status string from the backend. May be any casing. */
  value: string | null | undefined;
  /** Optional override — display this text instead of the mapped label. */
  displayText?: string;
  /** Optional extra className (e.g. `t-xs` for size tweaks). */
  className?: string;
  /** Optional title (tooltip). */
  title?: string;
}

/**
 * Render `value` as a styled chip.
 *
 *   - Known values → the mapped badge class + label (existing look).
 *   - Unmapped values → `badge-neutral` with the original text as
 *     the label, so unexpected backend values stay visible but
 *     don't break the row's visual rhythm.
 *   - null / empty → em-dash so the cell stays a stable width.
 */
export function StatusChip({
  value,
  displayText,
  className,
  title,
}: StatusChipProps): JSX.Element {
  const raw = (value ?? '').toString().trim();
  if (!raw) {
    return <span className="t-muted">—</span>;
  }

  const key = normalise(raw);
  const mapped = KNOWN_STATUS_MAP[key];

  const badgeClass = mapped?.badgeClass ?? 'badge-neutral';
  const label = displayText ?? mapped?.label ?? raw;
  const cls = `badge ${badgeClass}${className ? ` ${className}` : ''}`;

  return (
    <span className={cls} data-tip={title}>
      {label}
    </span>
  );
}

/**
 * Convenience helper — returns the badge class alone for callers
 * that already have their own `<span className={...}>` shell.
 */
export function statusChipClass(value: string | null | undefined): string {
  const raw = (value ?? '').toString().trim();
  if (!raw) return 'badge-neutral';
  const key = normalise(raw);
  return KNOWN_STATUS_MAP[key]?.badgeClass ?? 'badge-neutral';
}
